import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

const EAKC_LETTERS: { letter: string; color: string }[] = [
  { letter: "E", color: COLORS.accentBlue },
  { letter: "A", color: COLORS.accentPurple },
  { letter: "K", color: COLORS.accentTeal },
  { letter: "C", color: COLORS.accentPink },
];

const PROMISE_LINES: { text: string; color: string }[] = [
  { text: "Upload your files.", color: COLORS.accentBlue },
  { text: "Ask questions.", color: COLORS.accentPurple },
  { text: "Get answers with sources.", color: COLORS.accentTeal },
];

export const Scene03MeetEAKC: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // --- Phase A: frame 0-300 "What if your company had a brain?" ---
  const phaseA = frame < 300;
  const phaseATextOpacity = interpolate(frame, [0, 30], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const phaseAFade = interpolate(frame, [260, 300], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Pulsing glow on "brain"
  const glowIntensity = interpolate(
    Math.sin(frame * 0.06),
    [-1, 1],
    [15, 35],
  );

  // --- Phase B: frame 300-600 "EAKC" reveal ---
  const phaseB = frame >= 300 && frame < 600;
  const phaseBFade = interpolate(frame, [560, 600], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Subtitle + tagline
  const subtitleOpacity = interpolate(frame, [340, 370], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const taglineOpacity = interpolate(frame, [370, 400], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase C: frame 600-900 "The Promise" ---
  const phaseC = frame >= 600;
  const phaseCFade = interpolate(frame, [860, 900], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill>
      <GridBackground />

      {/* Phase A: The Question */}
      {phaseA && (
        <AbsoluteFill style={{ opacity: phaseAFade }}>
          <div
            style={{
              position: "absolute",
              top: 380,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.body,
              fontSize: 36,
              color: COLORS.textSecondary,
              opacity: phaseATextOpacity,
            }}
          >
            What if your company
          </div>
          <div
            style={{
              position: "absolute",
              top: 430,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.heading,
              fontSize: 72,
              fontWeight: 700,
              color: COLORS.accentTeal,
              textShadow: `0 0 ${glowIntensity}px ${COLORS.accentTeal}80, 0 0 ${glowIntensity * 2}px ${COLORS.accentTeal}40`,
              opacity: phaseATextOpacity,
            }}
          >
            had a brain?
          </div>
        </AbsoluteFill>
      )}

      {/* Phase B: EAKC Reveal */}
      {phaseB && (
        <AbsoluteFill style={{ opacity: phaseBFade }}>
          {/* EAKC letters */}
          <div
            style={{
              position: "absolute",
              top: 360,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 20,
            }}
          >
            {EAKC_LETTERS.map((item, i) => {
              const letterDelay = 300 + i * 10;
              const letterProgress = spring({
                frame: frame - letterDelay,
                fps,
                config: SPRING.bouncy,
              });
              const letterOpacity = interpolate(
                letterProgress,
                [0, 1],
                [0, 1],
              );
              const letterScale = interpolate(
                letterProgress,
                [0, 1],
                [0.3, 1],
              );
              return (
                <span
                  key={item.letter}
                  style={{
                    fontFamily: FONTS.heading,
                    fontSize: 120,
                    fontWeight: 700,
                    color: item.color,
                    textShadow: `0 0 20px ${item.color}80, 0 0 40px ${item.color}40`,
                    opacity: letterOpacity,
                    transform: `scale(${letterScale})`,
                    display: "inline-block",
                  }}
                >
                  {item.letter}
                </span>
              );
            })}
          </div>

          {/* Subtitle */}
          <div
            style={{
              position: "absolute",
              top: 500,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.body,
              fontSize: 24,
              color: COLORS.textSecondary,
              letterSpacing: 6,
              textTransform: "uppercase" as const,
              opacity: subtitleOpacity,
            }}
          >
            Enterprise AI Knowledge Chat
          </div>

          {/* Tagline */}
          <div
            style={{
              position: "absolute",
              top: 550,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.body,
              fontSize: 18,
              color: COLORS.textMuted,
              opacity: taglineOpacity,
            }}
          >
            Your company&apos;s brain, powered by AI
          </div>
        </AbsoluteFill>
      )}

      {/* Phase C: The Promise */}
      {phaseC && (
        <AbsoluteFill style={{ opacity: phaseCFade }}>
          {PROMISE_LINES.map((line, i) => {
            const lineDelay = 600 + i * 20;
            const lineProgress = spring({
              frame: frame - lineDelay,
              fps,
              config: SPRING.gentle,
            });
            const lineOpacity = interpolate(lineProgress, [0, 1], [0, 1]);
            const lineY = interpolate(lineProgress, [0, 1], [20, 0]);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: 380 + i * 60 + lineY,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  fontFamily: FONTS.body,
                  fontSize: 28,
                  fontWeight: 600,
                  color: line.color,
                  opacity: lineOpacity,
                }}
              >
                {line.text}
              </div>
            );
          })}
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
